// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@loa/ui-components';
import { ChevronDownIcon } from '../loa';
import type { ProposalType } from '../../types/proposal';
import './NewProposalButton.css';

export interface NewProposalButtonProps {
  /** Called with the chosen proposal type when a menu option is picked. */
  onSelect: (type: ProposalType) => void;
  disabled?: boolean;
  /**
   * Shows the "Breakthrough Report" option — gated by the tenant's
   * `proposals.breakthrough-report.enabled` feature-flag probe. Defaults to
   * false so the option is hidden until the caller confirms it's active.
   */
  breakthroughReportEnabled?: boolean;
}

const BASE_OPTIONS: Array<{ type: ProposalType; label: string; testId: string }> = [
  { type: 'PENSION', label: 'Pension', testId: 'new-pension-proposal' },
  { type: 'FUND_INVESTMENT', label: 'Investment', testId: 'new-fund-investment-proposal' },
  { type: 'PROTECTION', label: 'Protection', testId: 'new-protection-proposal' },
];

const BREAKTHROUGH_REPORT_OPTION: { type: ProposalType; label: string; testId: string } = {
  type: 'BREAKTHROUGH_REPORT',
  label: 'Breakthrough Report',
  testId: 'new-breakthrough-report-proposal',
};

/**
 * Primary "New Proposal" button that opens a small type-chooser menu —
 * mirrors the Requests tab's header-action pattern (NudgeOutstandingButton's
 * portalled popover) so the Proposals card header reads identically.
 */
export const NewProposalButton: React.FC<NewProposalButtonProps> = ({
  onSelect,
  disabled = false,
  breakthroughReportEnabled = false,
}) => {
  const options = breakthroughReportEnabled
    ? [...BASE_OPTIONS, BREAKTHROUGH_REPORT_OPTION]
    : BASE_OPTIONS;
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!open) return;
    const updatePosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      setPosition({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        popoverRef.current &&
        !popoverRef.current.contains(t) &&
        triggerRef.current &&
        !triggerRef.current.contains(t)
      ) {
        setOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const select = (type: ProposalType) => {
    setOpen(false);
    onSelect(type);
  };

  const popover =
    open && position
      ? createPortal(
          <div
            ref={popoverRef}
            className="new-proposal__menu"
            role="menu"
            style={{ top: position.top, left: position.left, minWidth: position.width }}
            data-testid="new-proposal-menu"
          >
            {options.map(({ type, label, testId }) => (
              <button
                key={type}
                type="button"
                role="menuitem"
                className="new-proposal__option"
                onClick={() => select(type)}
                data-testid={testId}
              >
                {label}
              </button>
            ))}
          </div>,
          document.body,
        )
      : null;

  return (
    <div ref={triggerRef} className="new-proposal">
      <Button
        variant="filled"
        size="lg"
        iconRight={<ChevronDownIcon />}
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="new-proposal-button"
      >
        New Proposal
      </Button>
      {popover}
    </div>
  );
};
